// =====================================================
// 📁 QuotationGlobalDiscountSection.tsx
// =====================================================

import { useEffect, useRef, useState } from 'react';

import { DollarSign, ListChecks, Percent, Trash2, X } from 'lucide-react';

interface Props {
  loading?: boolean;

  initialValue?: number;

  initialTarget?: 'PRODUCTS' | 'TOTAL';

  onApply?: (discountPercent: number, target: 'PRODUCTS' | 'TOTAL') => void;

  onRemove?: () => void;
}

const QuotationGlobalDiscountSection: React.FC<Props> = ({
  loading = false,

  initialValue = 0,

  initialTarget = 'PRODUCTS',

  onApply,

  onRemove,
}) => {
  // =====================================================
  // 🔹 State
  // =====================================================

  const [expanded, setExpanded] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const [discountPercent, setDiscountPercent] = useState(
    initialValue > 0 ? initialValue.toString() : ''
  );

  const [target, setTarget] = useState<'PRODUCTS' | 'TOTAL'>(initialTarget);

  // =====================================================
  // 🔹 Handlers
  // =====================================================

  const handleToggle = () => {
    setExpanded(true);
  };

  const handleCancel = () => {
    setExpanded(false);

    setDiscountPercent(initialValue > 0 ? initialValue.toString() : '');
  };

  const handleApply = () => {
    const parsed = Number(discountPercent);

    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      return;
    }

    onApply?.(parsed, target);

    setExpanded(false);
  };

  const handleRemove = () => {
    onRemove?.();

    setDiscountPercent('');

    setExpanded(false);
  };

  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [expanded]);

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <div className="mb-5">
      {/* ===================================================== */}
      {/* 🔹 Trigger */}
      {/* ===================================================== */}

      {!expanded && (
        <button
          onClick={handleToggle}
          className="
          flex
          w-full
          items-center
          justify-center
          gap-2

          rounded-xl

          border-2
          border-dashed
          border-primary/50

          bg-primary/10

          px-4
          py-3

          text-sm
          font-semibold

          text-primary

          transition-all
          duration-200

          hover:border-primary/70
          hover:bg-primary/20
        "
        >
          <Percent size={16} />

          {initialValue > 0
            ? `Editar descuento global (${initialValue}%)`
            : 'Aplicar descuento global'}
        </button>
      )}

      {/* ===================================================== */}
      {/* 🔹 Expanded */}
      {/* ===================================================== */}

      {expanded && (
        <div
          className="
          rounded-2xl

          border
          border-stroke

          bg-white

          p-4

          shadow-sm

          dark:border-form-strokedark
          dark:bg-meta-4
        "
        >
          {/* ===================================================== */}
          {/* 🔹 Header */}
          {/* ===================================================== */}

          <div
            className="
            mb-4

            flex
            items-start
            justify-between
          "
          >
            <div>
              <h4
                className="
                text-sm
                font-semibold

                text-black

                dark:text-white
              "
              >
                Descuento Global
              </h4>

              <p
                className="
                mt-1

                text-xs

                text-body

                dark:text-bodydark1
              "
              >
                Aplicar descuento a toda la cotización.
              </p>
            </div>

            <button
              onClick={handleCancel}
              className="
              rounded-lg

              p-2

              text-body

              transition-all
              duration-200

              hover:bg-danger/10
              hover:text-danger
            "
            >
              <X size={16} />
            </button>
          </div>

          {/* ===================================================== */}
          {/* 🔹 Destino del descuento */}
          {/* ===================================================== */}

          <div className="mb-3 flex gap-2">
            <button
              type="button"
              onClick={() => setTarget('PRODUCTS')}
              className={`flex flex-1 items-start justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-xs transition-all duration-200 ${
                target === 'PRODUCTS'
                  ? 'border-primary bg-primary/10 text-primary shadow-sm'
                  : 'border-stroke text-black hover:border-primary/40 hover:bg-primary/5 dark:border-strokedark dark:text-white'
              }`}
            >
              <span>
                <span className="block font-semibold">A los productos</span>
                <span className="mt-0.5 block text-[11px] text-body dark:text-bodydark1">
                  Se reparte por línea. Respeta exclusiones.
                </span>
              </span>

              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                  target === 'PRODUCTS'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-body dark:bg-boxdark-2 dark:text-bodydark1'
                }`}
              >
                <ListChecks size={14} />
              </span>
            </button>

            <button
              type="button"
              onClick={() => setTarget('TOTAL')}
              className={`flex flex-1 items-start justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-xs transition-all duration-200 ${
                target === 'TOTAL'
                  ? 'border-primary bg-primary/10 text-primary shadow-sm'
                  : 'border-stroke text-black hover:border-primary/40 hover:bg-primary/5 dark:border-strokedark dark:text-white'
              }`}
            >
              <span>
                <span className="block font-semibold">Al total</span>
                <span className="mt-0.5 block text-[11px] text-body dark:text-bodydark1">
                  No toca los productos. Se resta del total.
                </span>
              </span>

              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                  target === 'TOTAL'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-body dark:bg-boxdark-2 dark:text-bodydark1'
                }`}
              >
                <DollarSign size={14} />
              </span>
            </button>
          </div>

          {/* ===================================================== */}
          {/* 🔹 Input + Actions */}
          {/* ===================================================== */}

          <div
            className="
            flex
            items-center
            gap-3
          "
          >
            {/* 🔹 Input */}

            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApply();
                  }
                }}
                placeholder="0"
                className="
                h-[44px]
                w-full

                rounded-xl

                border
                border-stroke

                bg-whiten

                px-4
                pr-10

                text-sm
                text-black

                outline-none

                transition-all

                focus:border-primary
                focus:ring-1
                focus:ring-primary/20

                dark:border-strokedark
                dark:bg-boxdark-2
                dark:text-white
              "
              />

              <span
                className="
                absolute
                right-3
                top-1/2

                -translate-y-1/2

                text-sm
                font-medium

                text-body
              "
              >
                %
              </span>
            </div>

            {/* 🔹 Apply */}

            <button
              onClick={handleApply}
              disabled={loading}
              className="
              flex
              h-[44px]
              items-center
              justify-center

              rounded-xl

              border
              border-primary

              bg-primary

              px-5

              text-sm
              font-semibold

              text-white

              shadow-md

              transition-all
              duration-200

              hover:bg-opacity-90

              disabled:cursor-not-allowed
              disabled:opacity-50
            "
            >
              Aplicar
            </button>

            {/* 🔹 Remove (solo si ya hay un descuento aplicado) */}

            {initialValue > 0 && (
              <button
                onClick={handleRemove}
                disabled={loading}
                title="Eliminar descuento global"
                className="
                flex
                h-[44px]
                w-[44px]
                shrink-0
                items-center
                justify-center

                rounded-xl

                border
                border-danger/30

                bg-danger/10

                text-danger

                shadow-sm

                transition-all
                duration-200

                hover:border-danger
                hover:bg-danger
                hover:text-white

                disabled:cursor-not-allowed
                disabled:opacity-50
              "
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationGlobalDiscountSection;
